import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { mockEndpoints } from "../mocks/appDetails";
import { generateClient } from "aws-amplify/api";
import { GQL_QUERIES } from "../queries";
import { resultsTransformer } from "../util/queryResultsTransformers";
import { GqlErrorTransformer } from "../util/GqlErrorTransformer";
import { connectChannel, getOnQueryStarted } from './event-channels';

const client = generateClient();

// Scholars endpoints resolve straight through to the GraphQL backend via getDataByQueryName.
function scholarsData(queryName) {
    return async (variables) => await getDataByQueryName(queryName, variables);
}

export const graphqlApi = createApi({
    reducerPath: "graphqlApi",
    baseQuery: fetchBaseQuery({ baseUrl: process.env.REACT_APP_API_URL }),
    endpoints: (builder) => ({
        // Homepage
        //
        getDashboardData: builder.query({ queryFn: async () => await getDataByQueryName("getDashboardDetails"), }),
        getNextApplicationForReviewer: builder.mutation({ queryFn: async variables => await getDataByQueryName("getNextApplicationForReviewer", variables), }),
        // TODO Delete mock stuff: getStudentDashboardData: builder.query({ queryFn: async (input) => mockEndpoints.getStudentDashboardData.queryFn(input), }),
        // TODO Delete mock stuff: getSearchResults: builder.query({ queryFn: async (searchParam) => await mockEndpoints.search.queryFn(searchParam), }),

        // Applicant Details
        getApplicationDetails: builder.query({
            //queryFn is just a promise, so normally we would hit an API.post call. in this case we just build a dummy promise that we can replace later with an API.post
            // queryFn: (variables) => mockEndpoints.studentDetails.queryFn(variables), 
            // queryFn: async (variables) => edmDocumentEvents(mockEndpoints.studentDetails.queryFn(variables)),
            queryFn: async (variables) => await getDataByQueryName("getApplicationDetails", variables),
            // async onQueryStarted(variables, { dispatch, queryFulfilled }) {
            async onQueryStarted(variables, { dispatch, getState }) {
                connectChannel('/documents/fetch', dispatch, getState);
                // const key = `${variables.applicantEmployeeId}-${variables.programId}-${variables.term}`;
                // recommendationHistoryListener(key);
            },
            providesTags: ["getApplicationDetails"],
            // queryFn: async (variables) => await edmDocumentEvents(await getDataByQueryName("getApplicationDetails", variables)),
        }),
        // has503 — Scholars applicant details (separate Scholars authorizer; same response shape).
        getScholarsApplicationDetails: builder.query({
            queryFn: async (variables) => await getDataByQueryName("getScholarsApplicationDetails", variables),
            async onQueryStarted(variables, { dispatch, getState }) {
                connectChannel('/documents/fetch', dispatch, getState);
            },
            providesTags: ["getApplicationDetails"],
        }),
        getDocuments: builder.query({ queryFn: async (variables) => await getDataByQueryName("fetchDocumentUrl", variables), }),
        getDocumentStatus: builder.query({ queryFn: async (variables) => await getDataByQueryName("getDocumentStatus", variables), }),
        searchDocuments: builder.query({
            queryFn: async (variables) => await getDataByQueryName("searchApplicationDocument", variables),
        }),
        applicationRestartReview     : builder.mutation({ queryFn: async variables => await getDataByQueryName("applicationRestartReview", variables), invalidatesTags: ["getApplicationDetails"], }),
        applicationAssignReview      : builder.mutation({ queryFn: async variables => await getDataByQueryName("applicationAssignReview", variables), invalidatesTags: ["getApplicationDetails"], }),
        applicationReassignReview    : builder.mutation({ queryFn: async variables => await getDataByQueryName("applicationReassignReview", variables), invalidatesTags: ["getApplicationDetails"], }),
        applicationUnassignReview    : builder.mutation({ queryFn: async variables => await getDataByQueryName("applicationUnassignReview", variables), invalidatesTags: ["getApplicationDetails"], }),
        applicationSubmitReview      : builder.mutation({ queryFn: async variables => await getDataByQueryName("applicationSubmitReview", variables), invalidatesTags: ["getApplicationDetails"], }),
        applicationSaveReview        : builder.mutation({ queryFn: async variables => await getDataByQueryName("applicationSaveReview", variables), invalidatesTags: ["getApplicationDetails"], }),
        applicationConflictOfInterest: builder.mutation({ queryFn: async variables => await getDataByQueryName("applicationConflictOfInterest", variables), invalidatesTags: ["getApplicationDetails"], }),
        adminOverride                : builder.mutation({ queryFn: async variables => await getDataByQueryName("adminOverride", variables), invalidatesTags: ["getApplicationDetails"], }),

        // Manage Applications
        getManageApplicationSidebarProgram: builder.query({
            queryFn: async () => await getDataByQueryName("getManageApplicationsSidebar"),
            providesTags: ["ManageApplicationsSidebarProgram"],
        }),
        getManageApplicationSidebarNumbersReviewer: builder.query({
            queryFn: (reviewerEmployeeId) => mockEndpoints.manageApplicationsSidebarReviewer.queryFn(reviewerEmployeeId),
            providesTags: ["ManageApplicationsSidebarReviewer"],
        }),
        listAssignableApplications: builder.query({
            queryFn: async (val) => await getDataByQueryName("listAssignableApplications", { input: val }),
            providesTags: ["list-assignable"],
        }),
        listAssignedApplications: builder.query({
            queryFn: async (val) => await getDataByQueryName("listAssignedApplications", { input: val }),
            providesTags: ["list-assigned"],
        }),
        bulkAssignUnassignApplications: builder.mutation({ queryFn: async variables => await getDataByQueryName("bulkAssignUnassignApplications", variables), }),

        // Reports
        getReport: builder.query({
            queryFn: async (variables) => await getDataByQueryName("getReport", variables),
            providesTags: ["getReport"],
        }),
        getAdminOverrideReasons: builder.query({ queryFn: async () => await getDataByQueryName("getAdminOverrideReasons"), }),
        approveRecommendations: builder.mutation({
            queryFn: async (variables) => await getDataByQueryName("approveRecommendations", variables),
            onQueryStarted: getOnQueryStarted('/reports/recommendations-approved'),
        }),
        submitStudentRequestProgramChangeWithdraw: builder.mutation({
            queryFn: async (variables) => await getDataByQueryName("submitStudentRequestProgramChangeWithdraw", variables),
            onQueryStarted: getOnQueryStarted('/reports/program-change-or-withdraw-complete'),
        }),
        publishApplications: builder.mutation({
            queryFn: async variables => await getDataByQueryName("publishApplications", variables),
            onQueryStarted: getOnQueryStarted('/reports/publish-complete'),
        }),
        approveDenyRequests: builder.mutation({ queryFn: async variables => await getDataByQueryName("approveDenyRequests", variables), }),

        // Reviewers // TODO Save to the actual database
        getReviewers                : builder.query   ({ queryFn: async (variables) => await getDataByQueryName("getReviewers", variables), providesTags: ["Reviewers"] }),
        getOneReviewer              : builder.query   ({ queryFn: async (employeeId) => await getDataByQueryName("getOneReviewer", {employeeId}), providesTags: ["Reviewer"], }),
        updateReviewer              : builder.mutation({ queryFn: async (variables) => await getDataByQueryName("updateReviewer", variables), invalidatesTags: ["Reviewer"], }),
        toggleActiveStatus          : builder.mutation({ queryFn: async (variables) => await getDataByQueryName("toggleActiveStatus", variables), invalidatesTags: ["Reviewers"], }),
        
        // Configurations
        getCriteria                 : builder.query   ({ queryFn: async () => await getDataByQueryName("getCriteria"), }),
        saveCriteria                : builder.mutation({ queryFn: async (variables) => await getDataByQueryName("saveCriteria", {rules: variables}), }),
        getRubrics                  : builder.query   ({ queryFn: async programId => await getDataByQueryName("getRubrics", {programId}), }),
        getRubricConfig             : builder.query   ({ queryFn: async () => await getDataByQueryName("getRubricConfig"), }),
        setActiveRubric             : builder.mutation({ queryFn: async variables => await getDataByQueryName("setActiveRubric", variables), }),
        createARubric               : builder.mutation({ queryFn: async programId => await getDataByQueryName("createARubric", {programId}), }),
        cloneRubric                 : builder.mutation({ queryFn: async variables => await getDataByQueryName("cloneRubric", variables), }),
        archiveRubric               : builder.mutation({ queryFn: async variables => await getDataByQueryName("archiveRubric", variables), }),
        deleteRubric                : builder.mutation({ queryFn: async variables => await getDataByQueryName("deleteRubric", variables), }),
        saveRubricV2                : builder.mutation({ queryFn: async variables => await getDataByQueryName("saveRubricV2", variables), }),

        // Exports
        exportGrid: builder.query({
            queryFn: async (variables) => await getDataByQueryName("exportGrid", variables),
            async onQueryStarted(variables, { dispatch }) {
                connectChannel('/documents/export', dispatch);
            },
        }),
        // Search - Basic & Advanced
        getApplicantAttributes      : builder.query   ({ queryFn: async () => await getDataByQueryName("getApplicantAttributes"), }),
        getChangeWorkflowOptions    : builder.query   ({ queryFn: async variables => await getDataByQueryName("getChangeWorkflowOptions", variables), }),
        getDistinctValuesForSearch  : builder.query   ({ queryFn: async attribute => await getDataByQueryName("getDistinctValuesForSearch", {attribute}), }),
        getProgramResolveOptions    : builder.query   ({ queryFn: async () => await getDataByQueryName("getProgramResolveOptions"), }),
        getStudentRequestReasons    : builder.query   ({ queryFn: async () => await getDataByQueryName("getStudentRequestReasons"), }),
        personSearch                : builder.query   ({ queryFn: async variables => await getDataByQueryName("personSearch", variables), }),
        searchApplicant             : builder.query   ({ queryFn: async employeeId => await getDataByQueryName("searchApplicant", {employeeId}), }),
        searchReviewer              : builder.query   ({ queryFn: async employeeId => await getDataByQueryName("searchReviewer", {employeeId}), }),
        searchForAllData            : builder.query   ({ queryFn: async variables => await getDataByQueryName("searchForAllData", variables), }),
        overrideWorkflowDecision    : builder.mutation({ queryFn: async variables => await getDataByQueryName("overrideWorkflowDecision", variables),
            onQueryStarted: getOnQueryStarted('/search/override-workflow-decision-complete'),
        }),
        changeWorkflow              : builder.mutation({ queryFn: async variables => await getDataByQueryName("changeWorkflow", variables),
            onQueryStarted: getOnQueryStarted('/search/change-workflow-complete'),
        }),
        changeRequested             : builder.mutation({ queryFn: async variables => await getDataByQueryName("changeRequested", variables), }),
        
        // ------------------------------------------------------------------
        // Admissions Cycles Configuration (U4-CFG). Keyed per-program tag invalidation.
        // ------------------------------------------------------------------
        getAdmissionsCycles: builder.query({
            queryFn: async ({ programId }) => await getDataByQueryName("getAdmissionsCycles", { programId }),
            providesTags: (result, error, { programId }) => [{ type: "AdmissionsCycles", id: programId }],
        }),
        startHonorsCycle: builder.mutation({
            queryFn: async (variables) => await getDataByQueryName("startHonorsCycle", variables),
            invalidatesTags: [{ type: "AdmissionsCycles", id: "honors" }],
        }),
        startScholarsCycle: builder.mutation({
            queryFn: async (variables) => await getDataByQueryName("startScholarsCycle", variables),
            invalidatesTags: [{ type: "AdmissionsCycles", id: "scholars" }],
        }),

        // System Wide Messages
        getSystemWideMessages: builder.query({
            queryFn: async () => await getDataByQueryName("getSystemWideMessages"),
            providesTags: ["systemMessages"],
        }),
        dismissSystemWideMessages: builder.mutation({
            queryFn: async (variables) => await getDataByQueryName("dismissSystemWideMessages", variables),
            invalidatesTags: ["systemMessages"],
        }),

        // ------------------------------------------------------------------
        // Scholars (U9a — staff). Single-attempt mutations (NFR Q5=A): no retry.
        // Tag invalidation refreshes queue / summary after writes.
        // ------------------------------------------------------------------
        getScholarsPrograms: builder.query({
            queryFn: scholarsData("getScholarsPrograms"),
            providesTags: ["ScholarsPrograms"],
        }),
        updateScholarsProgram: builder.mutation({
            queryFn: scholarsData("updateScholarsProgram"),
            invalidatesTags: ["ScholarsPrograms"],
        }),
        setScholarsProgramCapacity: builder.mutation({
            queryFn: scholarsData("setScholarsProgramCapacity"),
            invalidatesTags: ["ScholarsPrograms"],
        }),
        openCloseScholarsProgram: builder.mutation({
            queryFn: scholarsData("openCloseScholarsProgram"),
            invalidatesTags: ["ScholarsPrograms"],
        }),
        setScholarsProgramPdny: builder.mutation({
            queryFn: scholarsData("setScholarsProgramPdny"),
            invalidatesTags: ["ScholarsPrograms", "ScholarsProgram", "ScholarsGlobalPdny"],
        }),
        getScholarsGlobalPdny: builder.query({
            queryFn: scholarsData("getScholarsGlobalPdny"),
            providesTags: ["ScholarsGlobalPdny"],
        }),
        setScholarsGlobalPdny: builder.mutation({
            queryFn: scholarsData("setScholarsGlobalPdny"),
            invalidatesTags: ["ScholarsGlobalPdny", "ScholarsPrograms"],
        }),
        // has570 — global Scholars review period (reviewer complete-by window, in days). Changing it moves the
        // read-time "Due"/past-due window for all programs, so invalidate the program/queue views and reports.
        getScholarsReviewPeriodDays: builder.query({
            queryFn: scholarsData("getScholarsReviewPeriodDays"),
            providesTags: ["ScholarsReviewPeriod"],
        }),
        setScholarsReviewPeriodDays: builder.mutation({
            queryFn: scholarsData("setScholarsReviewPeriodDays"),
            invalidatesTags: ["ScholarsReviewPeriod", "ScholarsPrograms", "ScholarsQueue", "getReport"],
        }),
        getScholarsProgram: builder.query({
            queryFn: scholarsData("getScholarsProgram"),
            providesTags: (result, error, { subProgramId }) => [{ type: "ScholarsProgram", id: subProgramId }],
        }),
        getScholarsProgramQueue: builder.query({
            queryFn: scholarsData("getScholarsProgramQueue"),
            providesTags: ["ScholarsQueue"],
        }),
        // has-inprogress — caller's own claimed applicants (Common Queue "In Progress" grid). Shares the
        // ScholarsQueue tag so pull/recommend/decision writes refresh it alongside the program cards.
        getScholarsReviewerInProgress: builder.query({
            queryFn: scholarsData("getScholarsReviewerInProgress"),
            providesTags: ["ScholarsQueue"],
        }),
        // has561 bugfix — the Common Queue program cards read getScholarsPrograms (tag "ScholarsPrograms")
        // for their New/In-Progress/Past-Due tallies. Any mutation that changes a program's queue population
        // (pull, assign, recommend/deny, decision) must invalidate "ScholarsPrograms" too, or the card counts
        // go stale until a manual page refresh.
        pullNextScholarsApplicant: builder.mutation({
            queryFn: scholarsData("pullNextScholarsApplicant"),
            invalidatesTags: ["ScholarsQueue", "ScholarsPrograms"],
        }),
        assignApplicationToProgram: builder.mutation({
            queryFn: scholarsData("assignApplicationToProgram"),
            invalidatesTags: ["ScholarsQueue", "ScholarsPrograms", "getApplicationDetails"],
        }),
        resumeLaterScholarsApplicant: builder.mutation({
            queryFn: scholarsData("resumeLaterScholarsApplicant"),
            invalidatesTags: ["ScholarsQueue", "getApplicationDetails"],
        }),
        // Review Cascade grid row action — ADMIN releases a reviewer's "saved for later" flag on an active
        // placement. Refresh the cascade queue grid (getReport) and the overview/queue counts (ScholarsQueue).
        releaseScholarsResumeLater: builder.mutation({
            queryFn: scholarsData("releaseScholarsResumeLater"),
            invalidatesTags: ["ScholarsQueue", "getReport", "getApplicationDetails"],
        }),
        submitScholarsRecommendation: builder.mutation({
            queryFn: scholarsData("submitScholarsRecommendation"),
            invalidatesTags: ["ScholarsQueue", "ScholarsPrograms", "getApplicationDetails"],
        }),
        // has615 — bulk-lock the selected Rec-to-Approve rows (isPendingProcessing = true) so the processing
        // icon shows while the Approve dialog's synchronous per-applicant loop runs. Invalidate getReport so
        // the grid refetches and the icons appear before the loop starts.
        lockScholarsPendingProcessing: builder.mutation({
            queryFn: scholarsData("lockScholarsPendingProcessing"),
            invalidatesTags: ["getReport"],
        }),
        recordScholarsDecision: builder.mutation({
            queryFn: scholarsData("recordScholarsDecision"),
            invalidatesTags: ["ScholarsQueue", "ScholarsPrograms", "ScholarsAlternateList", "getApplicationDetails"],
        }),
        releaseScholarsToReview: builder.mutation({
            queryFn: scholarsData("releaseScholarsToReview"),
            // Refresh the Preferences Received report immediately so the user sees the action was triggered.
            // No completion event exists for the cascade (fire-and-forget, per-applicant async processing).
            invalidatesTags: ["ScholarsQueue", "ScholarsPrograms", "getReport"],
        }),
        listScholarsReviewers: builder.query({
            queryFn: scholarsData("listScholarsReviewers"),
            providesTags: ["ScholarsReviewers"],
        }),
        grantScholarsReviewerAccess: builder.mutation({
            queryFn: scholarsData("grantScholarsReviewerAccess"),
            invalidatesTags: ["ScholarsReviewers"],
        }),
        revokeScholarsReviewerAccess: builder.mutation({
            queryFn: scholarsData("revokeScholarsReviewerAccess"),
            invalidatesTags: ["ScholarsReviewers"],
        }),
        // U1-MSR — Manage Scholars Reviewers
        listScholarsReviewerCandidates: builder.query({
            queryFn: scholarsData("listScholarsReviewerCandidates"),
            providesTags: ["ScholarsReviewers"],
        }),
        setScholarsReviewerQuota: builder.mutation({
            queryFn: scholarsData("setScholarsReviewerQuota"),
            invalidatesTags: ["ScholarsReviewers"],
        }),
        getScholarsReviewerQuota: builder.query({
            queryFn: scholarsData("getScholarsReviewerQuota"),
            providesTags: ["ScholarsReviewers"],
        }),
        getScholarsAlternateList: builder.query({
            queryFn: scholarsData("getScholarsAlternateList"),
            providesTags: ["ScholarsAlternateList"],
        }),
        admitScholarsFromAlternate: builder.mutation({
            queryFn: scholarsData("admitScholarsFromAlternate"),
            invalidatesTags: ["ScholarsAlternateList", "ScholarsQueue", "ScholarsPrograms"],
        }),
        // has552 — ADMIN bulk-deny of opt-in applicants from the Opt-In List. Invalidates the alternate list
        // (denied applicants drop off) and the report cache (they appear on Recommendations to Approve).
        bulkDenyScholarsFromAlternate: builder.mutation({
            queryFn: scholarsData("bulkDenyScholarsFromAlternate"),
            invalidatesTags: ["getReport"],
        }),
        // has552 — ADMIN finalizes a pending final-deny on Recommendations to Approve (-> SCH_FINAL_DENY,
        // pending publish). Refresh the report so the finalized applicant drops off rec-to-approve.
        // has572 — Advanced Search bulk admin recommendation (Scholars). Async state machine; refresh the
        // search grid / report caches when it starts (applicants begin transitioning as the run runs).
        bulkAdminDenyScholars: builder.mutation({
            queryFn: scholarsData("bulkAdminDenyScholars"),
            // Register a navbar job (spinner -> done/failed) + subscribe to the completion channel; the
            // handler also refreshes the Opt-In List / reports when the run finishes. Mirrors overrideWorkflowDecision.
            onQueryStarted: getOnQueryStarted('/search/scholars-bulk-admin-rec-complete'),
            invalidatesTags: ["getReport", "ScholarsAlternateList", "ScholarsQueue", "ScholarsPrograms"],
        }),
        bulkAdminAdmitScholars: builder.mutation({
            queryFn: scholarsData("bulkAdminAdmitScholars"),
            onQueryStarted: getOnQueryStarted('/search/scholars-bulk-admin-rec-complete'),
            invalidatesTags: ["getReport", "ScholarsAlternateList", "ScholarsQueue", "ScholarsPrograms"],
        }),
        // Single-applicant administrative recommendation (Scholars applicant-detail Actions menu). Synchronous
        // (no state machine / completion channel), so no onQueryStarted. Refresh the applicant detail (status
        // changes) plus the queue/program/alternate/report caches the bulk admin rec also touches.
        adminRecommendScholar: builder.mutation({
            queryFn: scholarsData("adminRecommendScholar"),
            invalidatesTags: ["getApplicationDetails", "ScholarsQueue", "ScholarsPrograms", "ScholarsAlternateList", "getReport"],
        }),
        finalizeScholarsDeny: builder.mutation({
            queryFn: scholarsData("finalizeScholarsDeny"),
            invalidatesTags: ["ScholarsQueue", "ScholarsPrograms", "getReport", "getApplicationDetails"],
        }),
        triggerScholarsPublish: builder.mutation({
            queryFn: scholarsData("triggerScholarsPublish"),
            // Register a navbar job (spinner -> done/failed) and subscribe to the completion channel.
            // The channel handler also refreshes the Publish report when the run finishes (published
            // applicants drop off). Mirrors triggerScholarsPreferencingRun.
            onQueryStarted: getOnQueryStarted('/reports/scholars-publish-complete'),
            // Also refresh immediately on trigger (applicants begin publishing as the run runs).
            invalidatesTags: ["getReport"],
        }),
        triggerScholarsPreferencingRun: builder.mutation({
            queryFn: scholarsData("triggerScholarsPreferencingRun"),
            // Register a navbar job (spinner -> done/failed) and subscribe to the completion channel.
            // The channel handler also refreshes the Not Preferenced report when the run finishes.
            onQueryStarted: getOnQueryStarted('/reports/scholars-preferencing-complete'),
            // Also refresh immediately on trigger (applicants begin transitioning as the run runs).
            invalidatesTags: ["getReport"],
        }),
        // --- Scholars Preferences (has504 — read applicant preferences on details page) ---
        getScholarsPreferences: builder.query({
            queryFn: async (variables) => await getDataByQueryName("getScholarsPreferences", variables),
            providesTags: ["ScholarsPreferences"],
        }),
        // has503 — This query drives the "Review and Decision" panel state (IN_REVIEW -> actionable,
        // RECOMMENDED -> read-only, etc.). Decision/action mutations (submitScholarsRecommendation,
        // recordScholarsDecision, finalizeScholarsDeny, assignApplicationToProgram, pullNextScholarsApplicant)
        // all invalidate "ScholarsQueue", so it MUST carry that tag or the panel serves a stale queue entry
        // after a decision is submitted (applicant appears un-decided when revisited). "ScholarsPreferences"
        // is also kept because preference adjustments can reshuffle an applicant's queue entries.
        getScholarsApplicantQueueEntries: builder.query({
            queryFn: async (variables) => await getDataByQueryName("getScholarsApplicantQueueEntries", variables),
            providesTags: ["ScholarsQueue", "ScholarsPreferences"],
        }),
        // has-history — Admin-only full audit-trail history timeline for an applicant. Read-only; no caching
        // tags needed beyond the default since it is a point-in-time admin lookup refreshed on each search.
        getScholarsApplicantHistory: builder.query({
            queryFn: scholarsData("getScholarsApplicantHistory"),
        }),
        // Review Cascade admin overview — per-applicant cascade state across ranked preferences. Shares the
        // ScholarsQueue tag so the advance action (and any decision/pull) refreshes it.
        getScholarsCascadeOverview: builder.query({
            queryFn: scholarsData("getScholarsCascadeOverview"),
            providesTags: ["ScholarsQueue"],
        }),
        // Review Cascade "move to next queue" — DENYs the active placement + advances the cascade. Refresh
        // the overview (ScholarsQueue) and any applicant-detail panels reflecting the placement.
        advanceScholarsCascade: builder.mutation({
            queryFn: scholarsData("advanceScholarsCascade"),
            invalidatesTags: ["ScholarsQueue", "ScholarsAlternateList", "getApplicationDetails"],
        }),
        // Bulk "move to next queue" from the per-program MRT row selection. Refresh the queue grid (getReport)
        // and the overview counts (ScholarsQueue).
        bulkAdvanceScholarsCascade: builder.mutation({
            queryFn: scholarsData("bulkAdvanceScholarsCascade"),
            invalidatesTags: ["ScholarsQueue", "ScholarsAlternateList", "getReport", "getApplicationDetails"],
        }),
        adjustScholarsPreferences: builder.mutation({
            queryFn: async (variables) => await getDataByQueryName("adjustScholarsPreferences", variables),
            invalidatesTags: ["ScholarsPreferences", "getApplicationDetails"],
        }),
        // --- Scholars Preference Change Request (student employee or admin submits proposed changes) ---
        submitPreferenceChangeRequest: builder.mutation({
            queryFn: async (variables) => await getDataByQueryName("submitPreferenceChangeRequest", variables),
        }),
        // --- Scholars Approve Preference Change Request (admin applies and closes the request) ---
        approvePreferenceChangeRequest: builder.mutation({
            queryFn: async (variables) => await getDataByQueryName("approvePreferenceChangeRequest", variables),
            invalidatesTags: ["ScholarsPreferences", "getApplicationDetails"],
        }),
        // --- Scholars student-request processing (SINGLE entry point: appeal approve/deny; program
        // change/withdraw), dispatched by requestType. Separate from the Honors approveDenyRequests /
        // studentRequestProcessed mutations (FR-22). Refresh the report grid, queue/outcome counts, and
        // any applicant-detail panels. ---
        processScholarsRequest: builder.mutation({
            queryFn: async (variables) => await getDataByQueryName("processScholarsRequest", variables),
            invalidatesTags: ["getReport", "ScholarsQueue", "ScholarsPrograms", "getApplicationDetails"],
        }),
        // --- Scholars student-request CREATION (SINGLE entry point: appeal / program change / withdraw).
        // The Person Search "flag" dialog routes here for Scholars applicants instead of the shared Honors
        // changeRequested mutation (FR-22). An APPEAL also carries the appealed subProgramId. Refresh the
        // request reports so a newly-created request shows immediately. ---
        createScholarsRequest: builder.mutation({
            queryFn: async (variables) => await getDataByQueryName("createScholarsRequest", variables),
            invalidatesTags: ["getReport", "getApplicationDetails"],
        }),
        // --- Preferencing simulation (hidden admin tool) ---
        // OPEN programs pool for the simulator's random rankings (term resolved server-side).
        getOpenScholarsPrograms: builder.query({
            queryFn: scholarsData("getOpenScholarsPrograms"),
            providesTags: ["ScholarsPrograms"],
        }),
    }),
});

// RTKQ auto-unwraps from data, still need to unwrap inner queryname wrapper though
async function getDataByQueryName(queryName, variables) {
    if (!GQL_QUERIES[queryName]) { throw `queryName '${queryName}' not found, please check imports`; } // eslint-disable-line
    const queryParameters = { query: GQL_QUERIES[queryName], variables };
    
    const apiCallAsFn = async () => (await client.graphql(queryParameters)).data[queryName];
    const dataFromApi = await GqlErrorTransformer(apiCallAsFn); // Execute API call, and capture+throw transformed error results
    const dataTransformed = resultsTransformer(dataFromApi, queryName);
    return { data: dataTransformed };
}

export const {
  // Dashboard
  useGetDashboardDataQuery, useLazyGetDashboardDataQuery, useGetStudentDashboardDataQuery, useGetSearchResultsQuery, useGetNextApplicationForReviewerMutation,
  
  // Application Details
  useGetApplicationDetailsQuery,
  useGetScholarsApplicationDetailsQuery,
  useGetDocumentsQuery, useLazyGetDocumentsQuery, useSearchDocumentsQuery,
  useApplicationRestartReviewMutation,
  useApplicationAssignReviewMutation, useApplicationReassignReviewMutation, useApplicationUnassignReviewMutation,
  useApplicationSubmitReviewMutation,
  useApplicationSaveReviewMutation,
  useApplicationConflictOfInterestMutation,
  useAdminOverrideMutation,
  
  // Manage Applications
  useGetManageApplicationSidebarProgramQuery,
  useListAssignableApplicationsQuery, useListAssignedApplicationsQuery,
  useBulkAssignUnassignApplicationsMutation,
  
  // Reports
  useGetReportQuery, useLazyGetReportQuery,
  useApproveRecommendationsMutation,
  useSubmitStudentRequestProgramChangeWithdrawMutation,
  usePublishApplicationsMutation,
  useApproveDenyRequestsMutation,
  
  // Reviewers
  useGetReviewersQuery, useToggleActiveStatusMutation,
  
  // Reviewer
  useGetOneReviewerQuery, useUpdateReviewerMutation,

  // Rubric Config
  useGetRubricsQuery, useLazyGetRubricsQuery, useGetRubricConfigQuery, useCreateARubricMutation, useArchiveRubricMutation, useSaveRubricV2Mutation, 
  useDeleteRubricMutation, useCloneRubricMutation, useSetActiveRubricMutation,
  useGetCriteriaQuery, useLazyGetCriteriaQuery, useSaveCriteriaMutation,

  // System Wide Messages
  useGetSystemWideMessagesQuery, useDismissSystemWideMessagesMutation,

  // Cycles
  useGetManageCyclesQuery,

  // Search
  useLazySearchApplicantQuery, useSearchApplicantQuery,
  useLazySearchReviewerQuery, useSearchReviewerQuery,
  useLazySearchForAllDataQuery, useSearchForAllDataQuery,
  useGetApplicantAttributesQuery, useGetDistinctValuesForSearchQuery,
  useGetStudentRequestReasonsQuery,
  usePersonSearchQuery, useLazyPersonSearchQuery,
  useGetProgramResolveOptionsQuery, useGetAdminOverrideReasonsQuery,
  useChangeRequestedMutation,

  // Exports
  useExportGridQuery, useLazyExportGridQuery,
  useChangeWorkflowMutation, useGetChangeWorkflowOptionsQuery,
  useOverrideWorkflowDecisionMutation,

  // Scholars (U9a)
  useGetScholarsProgramsQuery,
  useUpdateScholarsProgramMutation, useSetScholarsProgramCapacityMutation,
  useOpenCloseScholarsProgramMutation, useSetScholarsProgramPdnyMutation,
  useGetScholarsGlobalPdnyQuery, useSetScholarsGlobalPdnyMutation,
  useGetScholarsReviewPeriodDaysQuery, useSetScholarsReviewPeriodDaysMutation,
  useGetScholarsProgramQuery,
  useGetScholarsProgramQueueQuery, usePullNextScholarsApplicantMutation,
  useGetScholarsReviewerInProgressQuery,
  useAssignApplicationToProgramMutation,
  useResumeLaterScholarsApplicantMutation,
  useReleaseScholarsResumeLaterMutation,
  useSubmitScholarsRecommendationMutation, useRecordScholarsDecisionMutation,
  useLockScholarsPendingProcessingMutation,
  useReleaseScholarsToReviewMutation,
  useListScholarsReviewersQuery, useGrantScholarsReviewerAccessMutation, useRevokeScholarsReviewerAccessMutation,
  useListScholarsReviewerCandidatesQuery, useSetScholarsReviewerQuotaMutation, useGetScholarsReviewerQuotaQuery,
  useGetScholarsAlternateListQuery, useAdmitScholarsFromAlternateMutation,
  useBulkDenyScholarsFromAlternateMutation, useFinalizeScholarsDenyMutation,
  useBulkAdminDenyScholarsMutation, useBulkAdminAdmitScholarsMutation,
  useAdminRecommendScholarMutation,
  useTriggerScholarsPublishMutation,
  useTriggerScholarsPreferencingRunMutation,
  useGetScholarsPreferencesQuery,
  useGetScholarsApplicantQueueEntriesQuery,
  useLazyGetScholarsApplicantHistoryQuery, useGetScholarsApplicantHistoryQuery,
  useGetScholarsCascadeOverviewQuery, useAdvanceScholarsCascadeMutation, useBulkAdvanceScholarsCascadeMutation,
  useAdjustScholarsPreferencesMutation,
  useSubmitPreferenceChangeRequestMutation,
  useApprovePreferenceChangeRequestMutation,
  useProcessScholarsRequestMutation,
  useCreateScholarsRequestMutation,
  // Scholars preferencing simulator (hidden admin tool)
  useGetOpenScholarsProgramsQuery, useLazyGetOpenScholarsProgramsQuery,
  // Admissions Cycles (U4-CFG)
  useGetAdmissionsCyclesQuery, useStartHonorsCycleMutation, useStartScholarsCycleMutation,

} = graphqlApi;


export const useGetApplicationDetailsQueryState = graphqlApi.endpoints.getApplicationDetails.useQueryState;
export const useGetScholarsApplicationDetailsQueryState = graphqlApi.endpoints.getScholarsApplicationDetails.useQueryState;
